import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import styles from './DetailPage.module.css';
import tableStyles from './AdminDashboard.module.css';
import { Button } from '../../components/common/Button/Button';
import { useAdmin } from './AdminContext';
import { useToast } from '../../contexts/ToastContext';
import { projectsAPI, usersAPI } from '../../services/api';

type TabId = 'overview' | 'documents' | 'settings' | 'team';

const statusLabels: Record<string, string> = {
  planning: 'Planning',
  active: 'Actief',
  paused: 'Gepauzeerd',
  completed: 'Afgerond',
  cancelled: 'Geannuleerd',
};

const moduleLabels: Record<string, string> = {
  dashboard: 'Dashboard',
  gebiedsanalyse: 'Gebiedsanalyse',
  sentiment: 'Sentiment',
  personas: "Persona's",
  strategy: 'Strategie',
  media: 'Middelen',
  werkplanning: 'Werkplanning',
  vragen: 'Vragen',
  relatiebeheer: 'Relatiebeheer',
  projectwebsite: 'Projectwebsite',
  participatie: 'Participatie',
  rapportage: 'Rapportage',
  'stakeholder-netwerk': 'Netwerk',
  vergunningen: 'Vergunningen',
  kennisbank: 'Kennisbank',
};

export const ProjectDetailPage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { projects, companies, users, refreshData } = useAdmin();
  const initialTab = (searchParams.get('tab') as TabId) || 'overview';
  const [activeTab, setActiveTab] = useState<TabId>(initialTab);
  const [documents, setDocuments] = useState<any[]>([]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [analyzeProgress, setAnalyzeProgress] = useState('');
  const [analyzingDocId, setAnalyzingDocId] = useState<string | null>(null);
  const [selectedDocIds, setSelectedDocIds] = useState<string[]>([]);
  const [analyzeOptions, setAnalyzeOptions] = useState({
    clearExistingPersonas: false,
    clearExistingStrategies: false,
    clearExistingWorkPlannings: false,
    clearExistingCoostoTags: false,
    triggerCoostoSync: true,
  });
  const [showAnalyzeOptions, setShowAnalyzeOptions] = useState(false);
  const [showAddUserDropdown, setShowAddUserDropdown] = useState(false);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const project = projects.find((p: any) => (p._id || p.id) === projectId);
  const companyName = project?.company?.name || companies.find(c => c._id === project?.company)?.name || '-';
  const companyId = project?.company?._id || project?.company;

  const teamMembers = project?.team || [];
  const projectUsers = users.filter((u: any) =>
    u.projects?.some((p: any) => (p._id || p) === projectId)
  );

  // All users that are assigned to this project (via user.projects)
  const allAssignedUserIds = new Set([
    ...projectUsers.map((u: any) => u._id || u.id),
    ...teamMembers.map((m: any) => m.user?._id || m.user).filter(Boolean),
    ...(project?.projectManager?._id ? [project.projectManager._id] : []),
  ]);

  // Available users to add (not yet assigned)
  const availableUsers = users.filter((u: any) => {
    const uid = u._id || u.id;
    if (allAssignedUserIds.has(uid)) return false;
    if (userSearchQuery) {
      const name = `${u.firstName || ''} ${u.lastName || ''}`.toLowerCase();
      return name.includes(userSearchQuery.toLowerCase()) || u.email?.toLowerCase().includes(userSearchQuery.toLowerCase());
    }
    return true;
  });

  const handleAddUserToProject = async (userId: string) => {
    try {
      const user = users.find((u: any) => (u._id || u.id) === userId);
      if (!user) return;
      const currentProjects = (user.projects || []).map((p: any) => p._id || p);
      await usersAPI.assignProjects(userId, [...currentProjects, projectId!]);
      toast.success('Gebruiker toegevoegd aan project');
      refreshData();
      setShowAddUserDropdown(false);
      setUserSearchQuery('');
    } catch (error: any) {
      toast.error('Toevoegen mislukt', error.response?.data?.error || 'Er ging iets mis.');
    }
  };

  const handleRemoveUserFromProject = async (userId: string) => {
    try {
      const user = users.find((u: any) => (u._id || u.id) === userId);
      if (!user) return;
      const currentProjects = (user.projects || []).map((p: any) => p._id || p);
      const updatedProjects = currentProjects.filter((pid: string) => pid !== projectId);
      await usersAPI.assignProjects(userId, updatedProjects);
      toast.success('Gebruiker verwijderd uit project');
      refreshData();
    } catch (error: any) {
      toast.error('Verwijderen mislukt', error.response?.data?.error || 'Er ging iets mis.');
    }
  };

  useEffect(() => {
    if (projectId) {
      fetchDocuments();
    }
  }, [projectId]);

  const fetchDocuments = async () => {
    if (!projectId) return;
    setLoadingDocs(true);
    try {
      const docs = await projectsAPI.getDocuments(projectId);
      setDocuments(docs || []);
    } catch (error) {
      console.error('Failed to fetch documents:', error);
    } finally {
      setLoadingDocs(false);
    }
  };

  const handleUploadFiles = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0 || !projectId) return;

    const validTypes = ['.pdf', '.doc', '.docx', '.txt'];
    const validFiles: File[] = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (file.size > 50 * 1024 * 1024) {
        toast.warning('Bestand te groot', `"${file.name}" is groter dan 50MB.`);
        continue;
      }
      const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
      if (!validTypes.includes(ext)) {
        toast.warning('Ongeldig formaat', `"${file.name}" wordt niet ondersteund. Upload PDF, DOC, DOCX of TXT.`);
        continue;
      }
      validFiles.push(file);
    }

    if (validFiles.length === 0) return;

    setUploading(true);
    try {
      const response = await projectsAPI.uploadDocuments(projectId, validFiles);
      toast.success('Upload geslaagd', `${response.documents.length} document(en) geüpload.`);
      await fetchDocuments();
      refreshData();
    } catch (error: any) {
      toast.error('Upload mislukt', error.response?.data?.error || error.message);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDeleteDocument = async (docId: string, docName: string) => {
    if (!window.confirm(`Weet je zeker dat je "${docName}" wilt verwijderen?`)) return;
    if (!projectId) return;

    try {
      await projectsAPI.deleteDocument(projectId, docId);
      setDocuments(prev => prev.filter(d => d._id !== docId));
      setSelectedDocIds(prev => prev.filter(id => id !== docId));
      toast.success('Verwijderd', `"${docName}" is verwijderd.`);
    } catch (error: any) {
      toast.error('Verwijderen mislukt', error.response?.data?.error || error.message);
    }
  };

  const handleAnalyzeSingleDoc = async (docId: string, docName: string) => {
    if (!projectId) return;
    setAnalyzingDocId(docId);
    try {
      const response = await projectsAPI.analyzeDocuments(projectId, {
        documentIds: [docId],
        ...analyzeOptions,
      });
      const summary = response.analysisResults.summary;
      toast.showToast('success', {
        title: 'Analyse voltooid',
        message: `"${docName}" is succesvol geanalyseerd.`,
        details: [
          { label: 'Documenten verwerkt', value: summary.documentsProcessed },
          { label: "Persona's geïmporteerd", value: summary.personasImported },
          { label: 'Strategieën geïmporteerd', value: summary.strategiesImported },
          { label: 'Werkplanningen geïmporteerd', value: summary.workPlanningsImported },
        ],
      });
      await fetchDocuments();
      refreshData();
    } catch (error: any) {
      toast.error('Analyse mislukt', error.response?.data?.error || error.message);
    } finally {
      setAnalyzingDocId(null);
    }
  };

  const handleToggleDocSelection = (docId: string) => {
    setSelectedDocIds(prev =>
      prev.includes(docId) ? prev.filter(id => id !== docId) : [...prev, docId]
    );
  };

  const handleSelectAllDocs = () => {
    if (selectedDocIds.length === documents.length) {
      setSelectedDocIds([]);
    } else {
      setSelectedDocIds(documents.map(d => d._id));
    }
  };

  const handleAnalyzeDocuments = async () => {
    if (selectedDocIds.length === 0 || !projectId) {
      toast.warning('Geen documenten geselecteerd', 'Selecteer minimaal één document om te analyseren.');
      return;
    }

    setAnalyzing(true);
    setAnalyzeProgress('Documenten downloaden...');

    try {
      setAnalyzeProgress('Tekst uit documenten extraheren...');
      await new Promise(resolve => setTimeout(resolve, 500));
      setAnalyzeProgress('Documenten analyseren met AI...');

      const response = await projectsAPI.analyzeDocuments(projectId, {
        documentIds: selectedDocIds,
        ...analyzeOptions,
      });

      setAnalyzeProgress('');
      const summary = response.analysisResults.summary;
      toast.showToast('success', {
        title: 'Analyse voltooid',
        message: 'Alle documenten zijn succesvol geanalyseerd.',
        details: [
          { label: 'Documenten verwerkt', value: summary.documentsProcessed },
          { label: "Persona's geïmporteerd", value: summary.personasImported },
          { label: 'Strategieën geïmporteerd', value: summary.strategiesImported },
          { label: 'Werkplanningen geïmporteerd', value: summary.workPlanningsImported },
          ...(summary.coostoTagsAdded > 0
            ? [{ label: 'Coosto tags toegevoegd', value: summary.coostoTagsAdded }]
            : []),
          ...(summary.coostoSyncStatus === 'success'
            ? [{ label: 'Coosto berichten gevonden', value: summary.coostoMessagesFound }]
            : []),
        ],
        duration: 10000,
      });
      await fetchDocuments();
      refreshData();
      setShowAnalyzeOptions(false);
    } catch (error: any) {
      toast.error('Analyse mislukt', error.response?.data?.error || error.message);
    } finally {
      setAnalyzing(false);
      setAnalyzeProgress('');
    }
  };

  if (!project) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p style={{ color: '#9DA6A8', marginBottom: '1rem' }}>Project niet gevonden.</p>
        <Button onClick={() => navigate('/admin/projects')}>Terug naar projecten</Button>
      </div>
    );
  }

  const handleDelete = async () => {
    if (window.confirm('Weet je zeker dat je dit project wilt verwijderen? Dit kan niet ongedaan worden gemaakt.')) {
      try {
        await projectsAPI.delete(project._id || project.id);
        toast.success('Project verwijderd');
        navigate('/admin/projects');
        refreshData();
      } catch (error: any) {
        toast.error('Verwijderen mislukt', error.response?.data?.error || 'Er ging iets mis.');
      }
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('nl-NL', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
  };

  const enabledModules = project.settings?.modules
    ? Object.entries(project.settings.modules).filter(([_, v]) => v).map(([k]) => k)
    : [];

  const tabs: { id: TabId; label: string; count?: number }[] = [
    { id: 'overview', label: 'Overzicht' },
    { id: 'documents', label: 'Documenten', count: documents.length },
    { id: 'settings', label: 'Instellingen' },
    { id: 'team', label: 'Team', count: teamMembers.length + projectUsers.length },
  ];

  return (
    <div>
      {/* Back link */}
      <div style={{ marginBottom: '1rem' }}>
        <Link
          to="/admin/projects"
          style={{ color: '#9DA6A8', textDecoration: 'none', fontSize: '0.875rem', display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="15 18 9 12 15 6" />
          </svg>
          Terug naar projecten
        </Link>
      </div>

      {/* Header */}
      <div className={styles.detailHeader}>
        <div className={styles.detailHeaderInfo}>
          {project.headerImage ? (
            <div className={styles.detailAvatar} style={{ borderRadius: '8px' }}>
              <img src={project.headerImage} alt={project.name} style={{ objectFit: 'cover' }} />
            </div>
          ) : (
            <div className={styles.avatarPlaceholder}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
              </svg>
            </div>
          )}
          <div className={styles.detailTitle}>
            <h1>{project.name}</h1>
            <div className={styles.detailSubtitle}>
              <span className={`${tableStyles.statusBadge} ${tableStyles[`status${project.status}`]}`}>
                {statusLabels[project.status] || project.status}
              </span>
              {companyId && (
                <Link to={`/admin/companies/${companyId}`}>
                  {companyName}
                </Link>
              )}
              {project.location?.city && <span>{project.location.city}</span>}
            </div>
          </div>
        </div>
        <div className={styles.detailHeaderActions}>
          <Button
            variant="secondary"
            onClick={() => navigate(`/project/${project._id || project.id}/dashboard`)}
          >
            Open project
          </Button>
          <Button onClick={() => navigate(`/admin/projects/${project._id || project.id}/edit`)}>
            Bewerken
          </Button>
          <button
            onClick={handleDelete}
            style={{
              padding: '0.5rem 1rem',
              background: 'none',
              border: '1px solid #E57373',
              color: '#E57373',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: 500,
            }}
          >
            Verwijderen
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className={styles.statsRow}>
        <div className={styles.miniStat}>
          <span className={styles.miniStatValue}>{documents.length}</span>
          <span className={styles.miniStatLabel}>Documenten</span>
        </div>
        <div className={styles.miniStat}>
          <span className={styles.miniStatValue}>{teamMembers.length + projectUsers.length}</span>
          <span className={styles.miniStatLabel}>Teamleden</span>
        </div>
        <div className={styles.miniStat}>
          <span className={styles.miniStatValue}>{enabledModules.length}</span>
          <span className={styles.miniStatLabel}>Modules</span>
        </div>
        <div className={styles.miniStat}>
          <span className={styles.miniStatValue}>{formatDate(project.startDate)}</span>
          <span className={styles.miniStatLabel}>Startdatum</span>
        </div>
      </div>

      {/* Tabs */}
      <div className={styles.detailTabs}>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            className={`${styles.detailTab} ${activeTab === tab.id ? styles.active : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}{tab.count !== undefined ? ` (${tab.count})` : ''}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className={styles.tabPanel}>
        {activeTab === 'overview' && (
          <div className={styles.infoGrid}>
            <div className={`${styles.infoCard} ${styles.infoCardFull}`}>
              <h3>Beschrijving</h3>
              <p className={styles.descriptionText}>
                {project.description || 'Geen beschrijving beschikbaar.'}
              </p>
            </div>

            <div className={styles.infoCard}>
              <h3>Projectdetails</h3>
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>Status</span>
                <span className={styles.infoValue}>{statusLabels[project.status] || project.status}</span>
              </div>
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>Locatie</span>
                <span className={styles.infoValue}>
                  {typeof project.location === 'object'
                    ? [project.location?.city, project.location?.address].filter(Boolean).join(', ') || '-'
                    : project.location || '-'}
                </span>
              </div>
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>Startdatum</span>
                <span className={styles.infoValue}>{formatDate(project.startDate)}</span>
              </div>
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>Einddatum</span>
                <span className={styles.infoValue}>{formatDate(project.endDate)}</span>
              </div>
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>Klant</span>
                <span className={styles.infoValue}>
                  {companyId ? (
                    <Link to={`/admin/companies/${companyId}`} style={{ color: '#E6007E', textDecoration: 'none' }}>
                      {companyName}
                    </Link>
                  ) : '-'}
                </span>
              </div>
            </div>

            <div className={styles.infoCard}>
              <h3>Actieve modules</h3>
              {enabledModules.length > 0 ? (
                <div className={styles.moduleBadges}>
                  {enabledModules.map((mod) => (
                    <span key={mod} className={`${styles.moduleBadge} ${styles.moduleBadgeActive}`}>
                      {moduleLabels[mod] || mod}
                    </span>
                  ))}
                </div>
              ) : (
                <p style={{ color: '#9DA6A8', fontSize: '0.9rem' }}>Geen modules geconfigureerd.</p>
              )}
            </div>

            {project.milestones && project.milestones.length > 0 && (
              <div className={`${styles.infoCard} ${styles.infoCardFull}`}>
                <h3>Mijlpalen</h3>
                {project.milestones.map((milestone: any, index: number) => (
                  <div key={index} className={styles.infoRow}>
                    <span className={styles.infoLabel}>
                      {milestone.completed ? '✓' : '○'} {milestone.name}
                    </span>
                    <span className={styles.infoValue}>
                      {milestone.date ? formatDate(milestone.date) : '-'}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'documents' && (
          <div>
            {/* Action buttons row */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginBottom: '1.5rem' }}>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.doc,.docx,.txt"
                multiple
                onChange={handleUploadFiles}
                style={{ display: 'none' }}
              />
              <Button
                variant="secondary"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading || analyzing}
              >
                {uploading ? (
                  <>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ animation: 'spin 1s linear infinite' }}>
                      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                    </svg>
                    Uploaden...
                  </>
                ) : (
                  <>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="17 8 12 3 7 8" />
                      <line x1="12" y1="3" x2="12" y2="15" />
                    </svg>
                    Documenten uploaden
                  </>
                )}
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  if (documents.length === 0) {
                    toast.warning('Geen documenten', 'Upload eerst documenten voordat je kunt analyseren.');
                    return;
                  }
                  if (selectedDocIds.length === 0) {
                    setSelectedDocIds(documents.map(d => d._id));
                  }
                  setShowAnalyzeOptions(true);
                }}
                disabled={uploading || analyzing || documents.length === 0}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8" />
                  <path d="M21 21l-4.35-4.35" />
                  <path d="M11 8v6M8 11h6" />
                </svg>
                Analyseren
              </Button>
            </div>

            {/* Analyze options panel */}
            {showAnalyzeOptions && !analyzing && (
              <div className={styles.infoCard} style={{ marginBottom: '1.5rem', border: '2px solid #E6007E' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                  <h3 style={{ margin: 0 }}>AI Analyse starten</h3>
                  <button
                    onClick={() => setShowAnalyzeOptions(false)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9DA6A8', fontSize: '1.25rem' }}
                  >
                    &times;
                  </button>
                </div>

                <p style={{ fontSize: '0.9rem', color: '#666D70', marginBottom: '1rem' }}>
                  Selecteer de documenten die je wilt analyseren. De AI genereert automatisch persona's, strategieën, werkplanningen en meer.
                </p>

                {/* Document selection */}
                <div style={{ marginBottom: '1rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <span style={{ fontWeight: 600, fontSize: '0.9rem', color: '#323232' }}>
                      {selectedDocIds.length} van {documents.length} documenten geselecteerd
                    </span>
                    <button
                      onClick={handleSelectAllDocs}
                      style={{ background: 'none', border: 'none', color: '#E6007E', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600 }}
                    >
                      {selectedDocIds.length === documents.length ? 'Deselecteer alles' : 'Selecteer alles'}
                    </button>
                  </div>
                  <div style={{ maxHeight: '150px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    {documents.map((doc: any) => (
                      <label
                        key={doc._id}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.375rem 0.5rem',
                          borderRadius: '6px', cursor: 'pointer',
                          background: selectedDocIds.includes(doc._id) ? 'rgba(230, 0, 126, 0.05)' : 'transparent',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={selectedDocIds.includes(doc._id)}
                          onChange={() => handleToggleDocSelection(doc._id)}
                          style={{ accentColor: '#E6007E', width: '16px', height: '16px' }}
                        />
                        <span style={{ fontSize: '0.875rem', color: '#323232' }}>
                          {doc.originalName || doc.name}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Options */}
                <details style={{ marginBottom: '1rem' }}>
                  <summary style={{ cursor: 'pointer', fontSize: '0.875rem', fontWeight: 600, color: '#666D70', marginBottom: '0.5rem' }}>
                    Geavanceerde opties
                  </summary>
                  <div style={{
                    display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.5rem',
                    padding: '0.75rem', background: '#FAFAFA', borderRadius: '8px', border: '1px solid #EEEEEE', marginTop: '0.5rem',
                  }}>
                    {[
                      { key: 'clearExistingPersonas' as const, label: "Bestaande persona's wissen" },
                      { key: 'clearExistingStrategies' as const, label: 'Bestaande strategieën wissen' },
                      { key: 'clearExistingWorkPlannings' as const, label: 'Bestaande werkplanningen wissen' },
                      { key: 'clearExistingCoostoTags' as const, label: 'Bestaande Coosto tags wissen' },
                      { key: 'triggerCoostoSync' as const, label: 'Coosto sync na import' },
                    ].map(({ key, label }) => (
                      <label key={key} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.875rem' }}>
                        <input
                          type="checkbox"
                          checked={analyzeOptions[key]}
                          onChange={() => setAnalyzeOptions(prev => ({ ...prev, [key]: !prev[key] }))}
                          style={{ accentColor: '#E6007E', width: '16px', height: '16px' }}
                        />
                        {label}
                      </label>
                    ))}
                  </div>
                </details>

                <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                  <Button variant="secondary" onClick={() => setShowAnalyzeOptions(false)}>
                    Annuleren
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleAnalyzeDocuments}
                    disabled={selectedDocIds.length === 0}
                  >
                    {`Analyseer ${selectedDocIds.length} document(en)`}
                  </Button>
                </div>
              </div>
            )}

            {/* Progress indicator */}
            {analyzing && (
              <div className={styles.infoCard} style={{ marginBottom: '1.5rem', border: '2px solid #E6007E' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#E6007E" strokeWidth="2" style={{ animation: 'spin 1s linear infinite' }}>
                    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                  </svg>
                  <span style={{ color: '#E6007E', fontWeight: 600 }}>{analyzeProgress}</span>
                </div>
                <div style={{ width: '100%', height: '6px', background: '#EEEEEE', borderRadius: '3px', overflow: 'hidden' }}>
                  <div style={{
                    height: '100%', width: '60%', borderRadius: '3px',
                    background: 'linear-gradient(90deg, #E6007E, #ff4d9a)',
                    animation: 'shimmer 1.5s infinite',
                  }} />
                </div>
                <p style={{ fontSize: '0.8rem', color: '#9DA6A8', marginTop: '0.5rem', textAlign: 'center' }}>
                  Dit kan even duren afhankelijk van het aantal en de grootte van de documenten...
                </p>
              </div>
            )}

            {/* Documents table */}
            {loadingDocs ? (
              <div className={tableStyles.loading}>Documenten laden...</div>
            ) : documents.length === 0 ? (
              <div className={tableStyles.emptyState}>
                <div className={tableStyles.emptyIcon}>
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#9DA6A8" strokeWidth="1.5">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                  </svg>
                </div>
                <h3>Geen documenten</h3>
                <p>Upload documenten om AI-analyse te starten.</p>
                <Button onClick={() => fileInputRef.current?.click()}>
                  Documenten uploaden
                </Button>
              </div>
            ) : (
              <div className={tableStyles.tableWrapper}>
                <table className={tableStyles.dataTable}>
                  <thead>
                    <tr>
                      <th>Bestandsnaam</th>
                      <th>Grootte</th>
                      <th>Geüpload op</th>
                      <th>Laatst geanalyseerd</th>
                      <th style={{ width: '140px', textAlign: 'center' }}>Acties</th>
                    </tr>
                  </thead>
                  <tbody>
                    {documents.map((doc: any) => (
                      <tr key={doc._id}>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#E6007E" strokeWidth="2">
                              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                              <polyline points="14 2 14 8 20 8" />
                            </svg>
                            <span style={{ color: '#323232', fontWeight: 500 }}>
                              {doc.originalName || doc.name}
                            </span>
                          </div>
                        </td>
                        <td>{formatFileSize(doc.size)}</td>
                        <td>
                          {doc.uploadedAt
                            ? new Date(doc.uploadedAt).toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric' })
                            : '-'}
                        </td>
                        <td>
                          {doc.lastAnalyzedAt ? (
                            <span style={{ color: '#4CAF50' }}>
                              {new Date(doc.lastAnalyzedAt).toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                            </span>
                          ) : (
                            <span style={{ color: '#9DA6A8' }}>Nog niet geanalyseerd</span>
                          )}
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <div style={{ display: 'flex', gap: '0.25rem', justifyContent: 'center' }}>
                            <button
                              onClick={() => handleAnalyzeSingleDoc(doc._id, doc.originalName || doc.name)}
                              disabled={analyzing || analyzingDocId !== null}
                              style={{
                                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                width: '32px', height: '32px', borderRadius: '6px',
                                color: analyzingDocId === doc._id ? '#9DA6A8' : '#4CAF50',
                                background: 'none', border: 'none',
                                cursor: analyzingDocId !== null ? 'not-allowed' : 'pointer',
                                transition: 'background 0.15s',
                              }}
                              onMouseEnter={(e) => { if (!analyzingDocId) e.currentTarget.style.background = 'rgba(76,175,80,0.08)'; }}
                              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                              title={analyzingDocId === doc._id ? 'Bezig met analyseren...' : `Analyseer ${doc.originalName || doc.name}`}
                            >
                              {analyzingDocId === doc._id ? (
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ animation: 'spin 1s linear infinite' }}>
                                  <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                                </svg>
                              ) : (
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <circle cx="11" cy="11" r="8" />
                                  <path d="M21 21l-4.35-4.35" />
                                  <path d="M11 8v6" />
                                  <path d="M8 11h6" />
                                </svg>
                              )}
                            </button>
                            <button
                              onClick={async (e) => {
                                e.stopPropagation();
                                try {
                                  const response = await fetch(doc.url);
                                  const blob = await response.blob();
                                  const url = window.URL.createObjectURL(blob);
                                  const a = document.createElement('a');
                                  a.href = url;
                                  a.download = doc.originalName || doc.name;
                                  document.body.appendChild(a);
                                  a.click();
                                  document.body.removeChild(a);
                                  window.URL.revokeObjectURL(url);
                                } catch {
                                  window.open(doc.url, '_blank');
                                }
                              }}
                              style={{
                                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                width: '32px', height: '32px', borderRadius: '6px',
                                color: '#E6007E', background: 'none', border: 'none',
                                cursor: 'pointer', transition: 'background 0.15s',
                              }}
                              onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(230,0,126,0.08)')}
                              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                              title={`Download ${doc.originalName || doc.name}`}
                            >
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                <polyline points="7 10 12 15 17 10" />
                                <line x1="12" y1="15" x2="12" y2="3" />
                              </svg>
                            </button>
                            <button
                              onClick={() => handleDeleteDocument(doc._id, doc.originalName || doc.name)}
                              style={{
                                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                width: '32px', height: '32px', borderRadius: '6px',
                                color: '#E57373', background: 'none', border: 'none', cursor: 'pointer',
                                transition: 'background 0.15s',
                              }}
                              onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(229,115,115,0.08)')}
                              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                              title={`Verwijder ${doc.originalName || doc.name}`}
                            >
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="3 6 5 6 21 6" />
                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'settings' && (
          <div className={styles.infoGrid}>
            <div className={styles.infoCard}>
              <h3>Projectinstellingen</h3>
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>Zichtbaarheid</span>
                <span className={styles.infoValue}>{project.visibility || 'team'}</span>
              </div>
              {project.budget && (
                <>
                  <div className={styles.infoRow}>
                    <span className={styles.infoLabel}>Budget</span>
                    <span className={styles.infoValue}>
                      {project.budget.currency || '€'} {project.budget.amount?.toLocaleString('nl-NL') || '-'}
                    </span>
                  </div>
                </>
              )}
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>Tags</span>
                <span className={styles.infoValue}>
                  {project.tags?.length > 0 ? project.tags.join(', ') : '-'}
                </span>
              </div>
            </div>

            <div className={styles.infoCard}>
              <h3>Notificaties</h3>
              {project.settings?.notifications ? (
                <>
                  <div className={styles.infoRow}>
                    <span className={styles.infoLabel}>Updates</span>
                    <span className={styles.infoValue}>{project.settings.notifications.updates ? 'Aan' : 'Uit'}</span>
                  </div>
                  <div className={styles.infoRow}>
                    <span className={styles.infoLabel}>Sentiment</span>
                    <span className={styles.infoValue}>{project.settings.notifications.sentiment ? 'Aan' : 'Uit'}</span>
                  </div>
                  <div className={styles.infoRow}>
                    <span className={styles.infoLabel}>Mijlpalen</span>
                    <span className={styles.infoValue}>{project.settings.notifications.milestones ? 'Aan' : 'Uit'}</span>
                  </div>
                </>
              ) : (
                <p style={{ color: '#9DA6A8', fontSize: '0.9rem' }}>Geen notificatie-instellingen.</p>
              )}
            </div>

            <div className={`${styles.infoCard} ${styles.infoCardFull}`}>
              <h3>Modules</h3>
              <p style={{ fontSize: '13px', color: '#64748b', margin: '0 0 12px' }}>Klik op een module om deze aan of uit te zetten voor dit project.</p>
              <div className={styles.moduleBadges}>
                {Object.entries(moduleLabels).map(([key, label]) => {
                  const optInModules = ['participatie', 'rapportage', 'stakeholder-netwerk', 'vergunningen', 'kennisbank', 'issue-radar'];
                  const enabled = optInModules.includes(key)
                    ? project.settings?.modules?.[key] === true
                    : project.settings?.modules?.[key] !== false;
                  return (
                    <span
                      key={key}
                      className={`${styles.moduleBadge} ${enabled ? styles.moduleBadgeActive : styles.moduleBadgeInactive}`}
                      style={{ cursor: 'pointer' }}
                      onClick={async () => {
                        try {
                          const updatedModules = { ...project.settings?.modules, [key]: !enabled };
                          await projectsAPI.update(project._id, { settings: { ...project.settings, modules: updatedModules } });
                          toast.success(`${label} ${!enabled ? 'ingeschakeld' : 'uitgeschakeld'}`);
                          refreshData();
                        } catch {
                          toast.error('Fout bij bijwerken module');
                        }
                      }}
                    >
                      {label}
                    </span>
                  );
                })}
              </div>
            </div>

            {project.coosto && (
              <div className={`${styles.infoCard} ${styles.infoCardFull}`}>
                <h3>Coosto integratie</h3>
                <div className={styles.infoRow}>
                  <span className={styles.infoLabel}>Project ID</span>
                  <span className={styles.infoValue}>{project.coosto.projectId || '-'}</span>
                </div>
                <div className={styles.infoRow}>
                  <span className={styles.infoLabel}>Auto-sync</span>
                  <span className={styles.infoValue}>{project.coosto.autoSync ? 'Aan' : 'Uit'}</span>
                </div>
                <div className={styles.infoRow}>
                  <span className={styles.infoLabel}>Interval</span>
                  <span className={styles.infoValue}>{project.coosto.syncInterval || 'manual'}</span>
                </div>
                {project.coosto.lastSync && (
                  <div className={styles.infoRow}>
                    <span className={styles.infoLabel}>Laatste sync</span>
                    <span className={styles.infoValue}>{formatDate(project.coosto.lastSync)}</span>
                  </div>
                )}
                {project.coosto.searchTags?.length > 0 && (
                  <div className={styles.infoRow}>
                    <span className={styles.infoLabel}>Search tags</span>
                    <span className={styles.infoValue}>
                      {project.coosto.searchTags.map((t: any) => t.name || t).join(', ')}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === 'team' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem', position: 'relative' }}>
              <Button onClick={() => setShowAddUserDropdown(!showAddUserDropdown)}>
                + Gebruiker toevoegen
              </Button>
              {showAddUserDropdown && (
                <div style={{
                  position: 'absolute', top: '100%', right: 0, marginTop: '0.5rem',
                  background: '#fff', borderRadius: '8px', border: '1px solid #EEEEEE',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)', width: '320px', zIndex: 10,
                  maxHeight: '320px', display: 'flex', flexDirection: 'column',
                }}>
                  <div style={{ padding: '0.75rem', borderBottom: '1px solid #EEEEEE' }}>
                    <input
                      type="text"
                      placeholder="Zoek gebruiker..."
                      value={userSearchQuery}
                      onChange={(e) => setUserSearchQuery(e.target.value)}
                      autoFocus
                      style={{
                        width: '100%', padding: '0.5rem 0.75rem', border: '1px solid #DDDDDD',
                        borderRadius: '6px', fontSize: '0.875rem', fontFamily: "'Open Sans', sans-serif",
                        outline: 'none', boxSizing: 'border-box',
                      }}
                    />
                  </div>
                  <div style={{ overflowY: 'auto', maxHeight: '240px' }}>
                    {availableUsers.length === 0 ? (
                      <div style={{ padding: '1rem', textAlign: 'center', color: '#9DA6A8', fontSize: '0.875rem' }}>
                        Geen gebruikers beschikbaar
                      </div>
                    ) : (
                      availableUsers.map((user: any) => (
                        <button
                          key={user._id || user.id}
                          onClick={() => handleAddUserToProject(user._id || user.id)}
                          style={{
                            display: 'flex', alignItems: 'center', gap: '0.75rem',
                            width: '100%', padding: '0.625rem 0.75rem',
                            background: 'none', border: 'none', borderBottom: '1px solid #F5F5F5',
                            cursor: 'pointer', textAlign: 'left', fontFamily: "'Open Sans', sans-serif",
                            transition: 'background 0.15s',
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(230,0,126,0.03)')}
                          onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                        >
                          <div style={{
                            width: '32px', height: '32px', borderRadius: '50%',
                            background: 'rgba(230, 0, 126, 0.1)', display: 'flex',
                            alignItems: 'center', justifyContent: 'center',
                            color: '#E6007E', fontWeight: 600, fontSize: '0.75rem', flexShrink: 0,
                          }}>
                            {(user.firstName || '?').charAt(0).toUpperCase()}
                          </div>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontWeight: 600, fontSize: '0.875rem', color: '#323232' }}>
                              {user.firstName} {user.lastName}
                            </div>
                            <div style={{ fontSize: '0.75rem', color: '#9DA6A8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {user.email}
                            </div>
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>

            {teamMembers.length === 0 && projectUsers.length === 0 ? (
              <div className={tableStyles.emptyState}>
                <h3>Geen teamleden</h3>
                <p>Voeg gebruikers toe aan dit project.</p>
              </div>
            ) : (
              <div className={tableStyles.tableWrapper}>
                <table className={tableStyles.dataTable}>
                  <thead>
                    <tr>
                      <th>Naam</th>
                      <th>E-mail</th>
                      <th>Rol</th>
                      <th>Project rol</th>
                      <th>Acties</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Project manager */}
                    {project.projectManager && (
                      <tr>
                        <td style={{ fontWeight: 600 }}>
                          {project.projectManager.firstName
                            ? `${project.projectManager.firstName} ${project.projectManager.lastName}`
                            : project.projectManager.name || '-'}
                        </td>
                        <td>{project.projectManager.email || '-'}</td>
                        <td>
                          <span className={`${tableStyles.roleBadge} ${tableStyles.roleprojectManager}`}>
                            Projectmanager
                          </span>
                        </td>
                        <td>Manager</td>
                        <td></td>
                      </tr>
                    )}
                    {/* Team members */}
                    {teamMembers.map((member: any, index: number) => {
                      const user = member.user;
                      if (!user) return null;
                      return (
                        <tr key={user._id || index}>
                          <td style={{ fontWeight: 600 }}>
                            {user.firstName ? `${user.firstName} ${user.lastName}` : user.name || '-'}
                          </td>
                          <td>{user.email || '-'}</td>
                          <td>
                            <span className={`${tableStyles.roleBadge} ${tableStyles[`role${user.role}`]}`}>
                              {user.role || '-'}
                            </span>
                          </td>
                          <td>{member.role || '-'}</td>
                          <td>
                            <button
                              className={tableStyles.deleteBtn}
                              onClick={() => handleRemoveUserFromProject(user._id)}
                            >
                              Verwijder
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                    {/* Additional assigned users */}
                    {projectUsers
                      .filter((u: any) => !teamMembers.some((m: any) => (m.user?._id || m.user) === (u._id || u.id)))
                      .map((user: any) => (
                        <tr key={user._id || user.id}>
                          <td style={{ fontWeight: 600 }}>
                            {user.firstName ? `${user.firstName} ${user.lastName}` : user.name || '-'}
                          </td>
                          <td>{user.email || '-'}</td>
                          <td>
                            <span className={`${tableStyles.roleBadge} ${tableStyles[`role${user.role}`]}`}>
                              {user.role || '-'}
                            </span>
                          </td>
                          <td>Toegewezen</td>
                          <td>
                            <button
                              className={tableStyles.deleteBtn}
                              onClick={() => handleRemoveUserFromProject(user._id || user.id)}
                            >
                              Verwijder
                            </button>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* CSS for spin animation */}
      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes shimmer { 0% { transform: translateX(-100%); } 100% { transform: translateX(200%); } }
      `}</style>
    </div>
  );
};
